import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronDownIcon, UserIcon } from '@heroicons/react/24/outline';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  console.log('Rendering Navbar: adjusting logo, links, and CTA');

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="bg-white fixed top-0 left-0 right-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img className="h-16 w-auto" src="/images/logo_1_lynx.png" alt="LessonLynx Logo" />
              <img className="h-10 w-auto ml-3" src="/images/lesson_lynx_header_text.png" alt="LessonLynx" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <Link to="/about-us" className="text-base font-medium text-gray-700 hover:text-blue-600">
              About Us
            </Link>
            <Link to="/pricing" className="text-base font-medium text-gray-700 hover:text-blue-600">
              Pricing
            </Link>
            {!isAuthenticated && (
              <Link to="/become-a-teacher" className="text-base font-medium text-gray-700 hover:text-blue-600">
                Teach with LessonLynx
              </Link>
            )}
            
            {isAuthenticated ? (
              <>
                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-base font-medium text-gray-700 hover:text-blue-600"
                  >
                    <UserIcon className="w-5 h-5" />
                    <span>{user?.firstName}</span>
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                  
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <Link
                        to={user?.role === 'teacher' ? '/teacher-dashboard' : '/student-dashboard'}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Dashboard
                      </Link>
                      {user?.role === 'admin' || user?.role === 'super_admin' ? (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Admin Panel
                        </Link>
                      ) : null}
                      <button
                        onClick={() => {
                          logout();
                          setIsUserMenuOpen(false);
                          navigate('/');
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-base font-medium text-gray-700 hover:text-blue-600">
                  Sign in
                </Link>
                <Link
                  to="/instrument-selection"
                  className="ml-6 px-6 py-2.5 text-base font-medium text-white bg-blue-600 border-2 border-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md transform hover:-translate-y-px"
                >
                  Find a teacher
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Toggle menu</span>
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'opacity-100 max-h-screen pb-4' : 'opacity-0 max-h-0 overflow-hidden'}`}
      >
        <div className="px-4 pt-2 pb-3 space-y-2">
          <Link
            to="/about-us"
            className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About Us
          </Link>
          <Link
            to="/pricing"
            className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Pricing
          </Link>
          
          {isAuthenticated ? (
            <>
              {/* Authenticated Mobile Menu */}
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="px-3 py-2 text-sm font-medium text-gray-500">
                  Signed in as {user?.firstName} {user?.lastName}
                </div>
                <Link
                  to={user?.role === 'teacher' ? '/teacher-dashboard' : '/student-dashboard'}
                  className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                {user?.role === 'admin' || user?.role === 'super_admin' ? (
                  <Link
                    to="/admin"
                    className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                ) : null}
                <button
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                    navigate('/');
                  }}
                  className="block w-full text-left py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
                >
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Unauthenticated Mobile Menu */}
              <Link
                to="/become-a-teacher"
                className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Teach with LessonLynx
              </Link>
              <Link
                to="/login"
                className="block py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md px-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign in
              </Link>
              <Link
                to="/instrument-selection"
                className="block w-full mt-3 py-3 text-center text-base font-medium text-white bg-blue-600 border-2 border-blue-600 rounded-full hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Find a teacher
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 